import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LoadingScreen.css';

// Text Decoding Component
const DecodingText = ({ text, className }) => {
    const [display, setDisplay] = useState('');
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*';

    useEffect(() => {
        let iteration = 0;
        const interval = setInterval(() => {
            setDisplay(
                text
                    .split('')
                    .map((letter, index) => {
                        if (index < iteration) return text[index];
                        return chars[Math.floor(Math.random() * chars.length)];
                    })
                    .join('')
            );

            if (iteration >= text.length) clearInterval(interval);
            iteration += 1 / 3; // Speed of decoding
        }, 30);
        return () => clearInterval(interval);
    }, [text]);

    return <span className={className}>{display}</span>;
};

const LoadingScreen = ({ isLoading, onLoadingComplete }) => {
    const [progress, setProgress] = useState(0);
    const [currentPhase, setCurrentPhase] = useState(0);

    const loadingPhases = useMemo(() => [
        { text: 'INITIALIZING NEURAL LINK', duration: 800 },
        { text: 'LOADING ASSETS', duration: 1000 },
        { text: 'COMPILING QUANTUM NODES', duration: 1200 },
        { text: 'OPTIMIZING INTERFACE', duration: 800 },
        { text: 'SYSTEM READY', duration: 400 }
    ], []);

    useEffect(() => {
        if (!isLoading) return;

        const totalDuration = loadingPhases.reduce((sum, phase) => sum + phase.duration, 0);
        let elapsed = 0;

        const interval = setInterval(() => {
            elapsed += 20;
            const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
            setProgress(newProgress);

            let phaseElapsed = 0;
            for (let i = 0; i < loadingPhases.length; i++) {
                phaseElapsed += loadingPhases[i].duration;
                if (elapsed <= phaseElapsed) {
                    setCurrentPhase(i);
                    break;
                }
            }

            if (newProgress >= 100) {
                clearInterval(interval);
                setTimeout(() => {
                    onLoadingComplete();
                }, 800);
            }
        }, 20);

        return () => clearInterval(interval);
    }, [isLoading, loadingPhases, onLoadingComplete]);

    return (
        <AnimatePresence>
            {isLoading && (
                <motion.div
                    className="loading-screen"
                    exit={{ opacity: 0, transition: { duration: 0.8 } }}
                >
                    {/* Unique Background */}
                    <div className="nebula-bg" />
                    <div className="star-field" />

                    <div className="content-wrapper">
                        {/* Top System Header */}
                        <div className="system-header">
                            <span className="blink-text">SYS.2026.OS</span>
                            <span className="live-status">LIVE CONNECTION</span>
                        </div>

                        {/* Holographic Logo Container */}
                        <div className="holo-container">
                            <div className="holo-ring outer"></div>
                            <div className="holo-ring inner"></div>
                            <img src="/logo.png" alt="Logo" className="holo-logo" />
                            <div className="scan-line"></div>
                        </div>

                        {/* Main Title with Decoding Effect */}
                        <div className="title-section">
                            <h1 className="main-title">
                                <DecodingText text="AIEC IITM" className="glitch-font" />
                            </h1>
                            <motion.p
                                className="subtitle"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                AI EXPERIENCE CENTRE
                            </motion.p>
                        </div>

                        {/* UNIQUE Loading Bar Section */}
                        <div className="loading-bar-wrapper">
                            {/* Stats Data */}
                            <div className="bar-header">
                                <span className="phase-text">{loadingPhases[currentPhase]?.text || 'PROCESSING'}</span>
                                <span className="percentage-text">{Math.round(progress)}%</span>
                            </div>

                            {/* The 'Fusion' Bar */}
                            <div className="fusion-bar-container">
                                <div className="fusion-bar-track"></div>
                                <motion.div
                                    className="fusion-bar-fill"
                                    style={{ width: `${progress}%` }}
                                >
                                    <div className="plasma-flow"></div>
                                    <div className="leading-edge-star"></div>
                                </motion.div>
                            </div>

                            {/* Decorative Tech Lines under bar */}
                            <div className="tech-lines">
                                <div className="line line-1"></div>
                                <div className="line line-2"></div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LoadingScreen;